import { getSidebarItem } from "../../../config/sidebarConfig";
import type { DashboardStats } from "../../../services/api";
import {
  dashboardStatsQuery,
  joinRequestsQuery,
  noticesQuery,
  usersQuery,
} from "../../../services/queries";
import { filterActionableJoinRequests } from "../../../utils/joinRequestUtils";
import { viewQuery } from "../scopedQuery";
import { dayLabel, isInRange } from "../dateRange";
import { mapNoticesToCalendarEventCores } from "../../../utils/noticeCalendarEvents";
import { openPageEntry } from "./openPage";
import { AnswerContext, defineEntry } from "./types";
import { capitalize, countOf, datedList, isAre, listOf, wasWere, type Dated } from "./wording";

const home = getSidebarItem("home");
const linkToHome = () => ({ label: `Open ${home.label}`, href: home.path });

// The same queries Home uses, so the answer is the value Home shows
const loadNotices = ({ user, token, queryClient }: AnswerContext) =>
  queryClient.fetchQuery(viewQuery(user, noticesQuery(user?.schoolId, token)));

const loadStats = ({ user, token, queryClient }: AnswerContext) =>
  queryClient.fetchQuery(viewQuery(user, dashboardStatsQuery(user?.schoolId, token)));

const notices = (n: number) => countOf(n, "Notice");
const drafts = (n: number) => countOf(n, "Draft");
const joinRequests = (n: number) => countOf(n, "Join request");
const events = (n: number) => countOf(n, "Event");

/** "Saturday, October 10 at 9:00 AM", or the day alone for an all-day Event. */
function whenOfEvent(start: Date, allDay: boolean): string {
  if (allDay) return dayLabel(start);
  const time = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${dayLabel(start)} at ${time}`;
}
type NoticeTypeCounts = DashboardStats["noticesByType"];

const typeList = (types: NoticeTypeCounts) =>
  listOf(types.map(({ type, count }) => `${count} ${type}`));

const typeSentence = (types: NoticeTypeCounts) => {
  const top = types[0];
  const event = types.find((item) => item.type === "Event");
  const poll = types.find((item) => item.type === "Poll");
  const compared =
    event && poll ? ` There are ${event.count} Events and ${poll.count} Polls.` : "";
  return `By type, your Notices are ${typeList(types)}. ${top.type} is the most-used type, with ${top.count}.${compared}`;
};

// Home only shows its numbers once the Organization is approved, as Notices does, so the
// numbers also need "notices": Organizations still waiting don't get them.
export const homeEntries = [
  openPageEntry("home", "It's your overview of recent Notices and what's coming up.", [
    "Where's my overview?",
    "Take me back to the start",
  ]),

  // Home's "Notices Created (Last 7 Days)" chart, which counts Drafts too
  defineEntry<{ count: number }>({
    id: "home.noticesCreated",
    sidebarItem: "home",
    alsoNeeds: ["notices"],
    examples: [
      "How many notices did we send this week?",
      "Notices sent this week",
      "How many notices went out this month?",
      "How many notices did we create last week?",
      "Number of notices sent this month",
      "How many notices did I send last month?",
      "Count the notices we sent this week",
      "Notices created in the last 7 days",
      "How many notices last month?",
      "How many notices were made this week?",
      "How many notices were created?",
    ],
    slots: { dateRange: "last7Days" },
    answer: {
      load: async ({ dateRange }, ctx) => {
        const all = await loadNotices(ctx);
        return { count: all.filter((n) => dateRange && isInRange(n.createdAt, dateRange)).length };
      },
      templates: [
        ({ count }, { dateRange }) => `${capitalize(notices(count))} ${wasWere(count)} created ${dateRange?.label}.`,
        ({ count }, { dateRange }) => `Your Organization created ${notices(count)} ${dateRange?.label}.`,
        ({ count }, { dateRange }) => `${capitalize(dateRange?.label ?? "")}, ${notices(count)} ${wasWere(count)} created.`,
      ],
      link: linkToHome,
    },
  }),

  // Home's "Notice Types Distribution" chart
  defineEntry<{ types: NoticeTypeCounts }>({
    id: "home.noticeTypes",
    sidebarItem: "home",
    alsoNeeds: ["notices"],
    examples: [
      "What types of notices have we sent?",
      "Which notice types do we use most?",
      "Which kinds of notices do we send most?",
      "Notice types breakdown",
      "How many urgent notices have we sent?",
      "How many event notices have we sent?",
      "How many information notices are there?",
      "How many image notices have we sent?",
      "Show me the notice types distribution",
      "What mix of notices do we send?",
      "Split of notices by type",
      "Do we send more events or info notices?",
      "do we send more events or polls",
      "Which type do we send most?",
      "What types of notices do we send?",
      "notice types distribution",
    ],
    answer: {
      load: async (_, ctx) => {
        const { noticesByType = [] } = await loadStats(ctx);
        return { types: [...noticesByType].sort((a, b) => b.count - a.count) };
      },
      templates: [
        ({ types }) =>
          types.length === 0
            ? "There are no Notices to show by type yet."
            : typeSentence(types),
        ({ types }) =>
          types.length === 0
            ? "You don't have any Notices to sort by type yet."
            : typeSentence(types),
        ({ types }) =>
          types.length === 0
            ? "You haven't sent any Notices yet, so there are no types to show."
            : typeSentence(types),
      ],
      link: linkToHome,
    },
  }),

  // Home's "You have N unpublished notices" banner
  defineEntry<{ count: number }>({
    id: "home.drafts",
    sidebarItem: "home",
    alsoNeeds: ["notices"],
    examples: [
      "How many drafts do I have?",
      "Do I have any drafts?",
      "Any unsent notices?",
      "How many unpublished notices are there?",
      "Notices I haven't sent yet",
      "How many draft notices do we have?",
      "Have I left any notices unsent?",
      "Are there saved notices waiting to be sent?",
      "Count my drafts",
      "Unfinished notices",
      "Show my unfinished notices",
      "Do we have unpublished drafts?",
      "Should I look at Drafts or Sent first?",
    ],
    answer: {
      load: async (_, ctx) => {
        const all = await loadNotices(ctx);
        return { count: all.filter((n) => n.isDraft && !n.isDeleted).length };
      },
      templates: [
        ({ count }) => `You have ${drafts(count)} right now.`,
        ({ count }) => `There ${isAre(count)} ${drafts(count)} waiting to be sent.`,
        ({ count }) => `Right now you have ${drafts(count)}: saved, but not sent.`,
      ],
      link: linkToHome,
    },
  }),

  defineEntry<{ count: number }>({
    id: "home.userCount",
    sidebarItem: "home",
    alsoNeeds: ["notices"],
    examples: [
      "How many users do we have?",
      "How many parents are on Noticeboard?",
      "What's our user count?",
      "How many people are invited?",
      "What is the All Users number?",
      "How many Users do we have?",
      "How many families are on Noticeboard?",
      "Count our users",
      "How many people use the app?",
      "What's the All Users total?",
    ],
    answer: {
      load: async (_, ctx) => ({ count: (await loadStats(ctx)).totalUsers }),
      templates: [
        ({ count }) => `Home shows All Users: ${countOf(count, "User")}. That figure doesn't say how many are parents, or how many invitations are still pending.`,
        ({ count }) => `The All Users card on Home says ${countOf(count, "User")}. That isn't a parent count, and it doesn't show pending invitations.`,
        ({ count }) => `You have ${countOf(count, "User")}, as Home shows under All Users. It doesn't split out parents or invitations still waiting.`,
      ],
      link: linkToHome,
    },
  }),

  // Home's "You have N join requests" banner. It uses the Users page's data, so only for people who can open Users.
  defineEntry<{ count: number }>({
    id: "home.joinRequests",
    sidebarItem: "home",
    alsoNeeds: ["users"],
    examples: [
      "Do we have any join requests?",
      "Are there any join requests?",
      "How many join requests are waiting?",
      "Pending join requests",
      "Has anyone asked to join?",
      "How many parents want to join?",
      "Are there requests to join waiting for me?",
      "Any new join requests?",
      "Count the join requests",
      "How many families have requested to join?",
      "Outstanding join requests",
      "Are there any join requests waiting?",
      "Any join requests waiting?",
      "Any join requests on home?",
    ],
    answer: {
      load: async (_, { user, token, queryClient }) => {
        const orgId = user?.schoolId || user?.orgId;
        const [requests, users] = await Promise.all([
          queryClient.fetchQuery(viewQuery(user, joinRequestsQuery(orgId, token))),
          queryClient.fetchQuery(viewQuery(user, usersQuery(orgId, token))),
        ]);
        return { count: filterActionableJoinRequests(requests, users).length };
      },
      templates: [
        ({ count }) => `You have ${joinRequests(count)} waiting.`,
        ({ count }) => `There ${isAre(count)} ${joinRequests(count)} to look at.`,
        ({ count }) => `${capitalize(joinRequests(count))} ${isAre(count)} waiting for you.`,
      ],
      link: linkToHome,
    },
  }),

  // Home's "Upcoming Events" count, naming the Events so "when?" and "what event?" have an answer
  defineEntry<{ count: number; named: string; first?: string; titles: string[] }>({
    id: "home.upcomingEvents",
    sidebarItem: "home",
    alsoNeeds: ["notices"],
    examples: [
      "What events are coming up?",
      "How many upcoming events do we have?",
      "Any events coming up?",
      "Upcoming events",
      "How many events are planned?",
      "Do we have events soon?",
      "Count our upcoming events",
      "How many events are ahead?",
      "Is anything coming up?",
      "Number of future events",
      "When is our next event?",
      "When is the next event?",
      "When's our next event?",
      "What is our next event?",
      "When is the next event on?",
      "How many upcoming events does Home show?",
    ],
    answer: {
      load: async (_, ctx) => {
        const [notices, stats] = await Promise.all([loadNotices(ctx), loadStats(ctx)]);
        const now = new Date();
        const upcoming = mapNoticesToCalendarEventCores(notices)
          .filter((event) => event.end > now)
          .sort((a, b) => a.start.getTime() - b.start.getTime());
        // One count for what's said and what's named: the Events it can list, or Home's figure when it can list none
        const count = upcoming.length || stats.upcomingEvents || 0;
        const listed: Dated[] = upcoming.map((event) => ({ title: event.fullTitle, when: whenOfEvent(event.start, event.allDay) }));
        return {
          count,
          named: listed.length ? datedList(listed) : "",
          first: listed[0]?.title,
          titles: listed.map((event) => event.title),
        };
      },
      templates: [
        ({ count, named }) =>
          count === 0
            ? "There are no Events coming up."
            : named
              ? `There ${isAre(count)} ${events(count)} coming up: ${named}.`
              : `There ${isAre(count)} ${events(count)} coming up.`,
        ({ count, named }) =>
          count === 0
            ? "You have no Events coming up."
            : named
              ? `You have ${events(count)} coming up: ${named}.`
              : `You have ${events(count)} coming up.`,
        ({ count, named }) =>
          count === 0
            ? "Nothing is coming up."
            : named
              ? `${capitalize(events(count))} ${isAre(count)} coming up: ${named}.`
              : `${capitalize(events(count))} ${isAre(count)} coming up.`,
      ],
      link: linkToHome,
      subjectOf: ({ first }) => first,
      mentionsOf: ({ titles }) => titles,
    },
  }),
];
